import React, { FC } from 'react';

import { Box } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';
import useInTrainingPilot from '@rover/react-lib/src/pages/training/hooks/useInTrainingPilot';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';
import {
  ICON_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { getServiceLinkDisplayName, NAVBAR_SERVICE_OPTIONS } from '../../constants';
import MenuItem from '../MenuItem';

export const SERVICES_MENU_ITEM_HEIGHT = 56;
export type Props = {
  showOurServices: boolean;
};

const fireEvent = (serviceType: ServiceTypeSlug): void => {
  try {
    fireDataLayerEvent({
      event: 'navigation-our-services-select-service',
      serviceType,
    });
  } catch (error) {
    captureError(error as Error);
  }
};

const MobileOurServices: FC<Props> = ({ showOurServices }) => {
  const theme = useTheme();
  const { serviceLinksForCurrentRoute: serviceLinks } = useServiceLinksShim();
  const { navigationIsEU } = useCountryConfigurationShim();
  const { shouldShowTrainingNavbarLink } = useInTrainingPilot();

  if (!serviceLinks) return null;

  const shouldShowGoodPupLink = !navigationIsEU && !shouldShowTrainingNavbarLink;

  return (
    <Box
      as="ul"
      pl="0x"
      width="100%"
      borderY={showOurServices ? `1px solid ${theme.colors.border.primary}` : 'none'}
      // The max-height just needs to be larger than the menu will be when open.
      // This number matches what we use in the django nav
      maxHeight={showOurServices ? '400px' : '0'}
      sx={{
        overflow: 'hidden',
        visibility: showOurServices ? 'visible' : 'hidden',
        transition: 'max-height 0.2s',
      }}
    >
      {NAVBAR_SERVICE_OPTIONS.map((serviceLandingType) => {
        const isTraining = serviceLandingType === ServiceLandingEnum.DOG_TRAINING;
        const serviceExistsForCountry = serviceLandingType in serviceLinks;
        if (!serviceExistsForCountry || (isTraining && !shouldShowGoodPupLink)) return null;

        const ServiceIcon = ICON_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

        return (
          <MenuItem
            href={serviceLinks[serviceLandingType]}
            onClick={() => fireEvent(SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingType])}
            key={serviceLandingType}
            icon={ServiceIcon}
            height={SERVICES_MENU_ITEM_HEIGHT}
          >
            {getServiceLinkDisplayName(serviceLandingType)}
          </MenuItem>
        );
      })}
    </Box>
  );
};

export default MobileOurServices;
